/**
 * NMS Sale Hero Component
 * "Watch video" opens its <dialog> through the Invoker Commands API, so the popup works
 * without this file in current browsers. This starts the video muted when the dialog opens,
 * pauses it on close, closes on a backdrop click, and opens the dialog where
 * Invoker Commands aren't supported yet.
 */

const NMS_SALE_HERO_SUPPORTS_COMMANDS = 'command' in HTMLButtonElement.prototype;

class NMSSaleHero extends window.NMSBaseClass {
  #controller = null;

  constructor(element) {
    super();

    // REQUIRED: Store element reference for scoped operations
    this.element = element;
    this.sectionId = element.dataset.sectionId || `section-${Date.now()}`;

    // REQUIRED: Register instance in global namespace for external access
    window.neemans = window.neemans || {};
    window.neemans.saleHero = window.neemans.saleHero || {};
    window.neemans.saleHero[this.sectionId] = this;
  }

  init() {
    // REQUIRED: Override base init to scope operations to this.element
    this.#setupComponentRefs();
    if (!this.refs.videoDialog) return;

    this.#bindComponentEvents();
  }

  destroy() {
    this.#controller?.abort();
    super.destroy();

    if (window.neemans.saleHero?.[this.sectionId]) {
      delete window.neemans.saleHero[this.sectionId];
    }
  }

  // REQUIRED: Component-scoped ref setup (override base class)
  #setupComponentRefs() {
    this.refs = {
      videoButton: this.element.querySelector('[ref="videoButton"]'),
      closeButton: this.element.querySelector('[ref="closeButton"]'),
      videoDialog: this.element.querySelector('[ref="videoDialog"]'),
    };
    // Desktop + mobile pair when both are set — CSS shows one per screen size
    this.refs.videos = Array.from(this.refs.videoDialog?.querySelectorAll('video') || []);
  }

  #bindComponentEvents() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const { videoButton, closeButton, videoDialog } = this.refs;

    if (NMS_SALE_HERO_SUPPORTS_COMMANDS) {
      // The browser opens the dialog itself, and "toggle" is the only signal that it did
      videoDialog.addEventListener(
        'toggle',
        event => {
          if (event.newState === 'open') this.#playMuted();
        },
        { signal }
      );
    } else {
      videoButton?.addEventListener('click', () => this.openVideo(), { signal });
      closeButton?.addEventListener('click', () => this.closeVideo(), { signal });
    }

    // "close" also fires for the Escape key, so the video never keeps playing unseen
    videoDialog.addEventListener('close', () => this.refs.videos.forEach(video => video.pause()), { signal });

    // Clicks on the ::backdrop land on the dialog element itself
    videoDialog.addEventListener(
      'click',
      event => {
        if (event.target === videoDialog) this.closeVideo();
      },
      { signal }
    );
  }

  #playMuted() {
    // The one CSS shows for this screen size (hidden videos have no layout box)
    const video = this.refs.videos.find(candidate => candidate.getClientRects().length > 0);
    if (!video) return;

    video.muted = true;
    video.play().catch(() => {
      // Autoplay refused - the native controls are still there to start it
    });

    this.dispatchNMSEvent('sale-hero-video-opened', { sectionId: this.sectionId });
  }

  // Public API for external access
  openVideo() {
    const { videoDialog } = this.refs;
    if (!videoDialog || videoDialog.open) return;

    videoDialog.showModal();
    if (!NMS_SALE_HERO_SUPPORTS_COMMANDS) this.#playMuted();
  }

  closeVideo() {
    this.refs.videoDialog?.close();
  }
}

// REQUIRED: Standard component initialization pattern
function initNMSSaleHero() {
  if (!window.NMSBaseClass) {
    console.error('NMS Sale Hero: NMSBaseClass not found. Make sure nms-base-class.js loads first.');
    return;
  }

  const elements = document.querySelectorAll('nms-sale-hero');

  for (const element of elements) {
    if (element.hasAttribute('data-initialized')) continue;

    try {
      const instance = new NMSSaleHero(element);
      instance.init();
      element.setAttribute('data-initialized', 'true');
    } catch (error) {
      console.error('Failed to initialize NMS Sale Hero:', error, element);
    }
  }
}

// Auto-initialize
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initNMSSaleHero);
} else {
  initNMSSaleHero();
}

// Re-initialize when new content is added (theme editor, AJAX, etc.)
document.addEventListener('nms:content-updated', initNMSSaleHero);
document.addEventListener('shopify:section:load', initNMSSaleHero);

// Export for debugging and manual initialization
window.initNMSSaleHero = initNMSSaleHero;

// Global API for accessing instances
window.getNMSSaleHero = function (sectionId) {
  if (sectionId) {
    return window.neemans?.saleHero?.[sectionId] || null;
  }
  return window.neemans?.saleHero || {};
};
